import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { QueryList } from '@/components/queries/QueryList';
import { QueryDetail } from '@/components/queries/QueryDetail';
import { FlyerLeadList } from '@/components/queries/FlyerLeadList';
import { FlyerLeadDetail } from '@/components/queries/FlyerLeadDetail';
import { IncomingLeadQueryModal } from '@/components/queries/IncomingLeadQueryModal';
import { startRinging, stopRinging } from '@/components/queries/ringing';
import { getCachedNotificationPreferences } from '@/hooks/useNotificationSummary';
import { adminSocket } from '@/lib/admin-socket';
import type { IncomingLeadQueryOffer } from '@/types/lead-query';
import { toast } from 'sonner';
import { Inbox, ArrowLeft, MessageSquare, FileImage } from 'lucide-react';
import { cn } from '@/lib/utils';

export type QueriesInboxFilter = 'all' | 'chat' | 'flyer';

export const QueriesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inboxFilter, setInboxFilter] = useState<QueriesInboxFilter>(() => {
    const inbox = searchParams.get('inbox');
    return inbox === 'flyer' || inbox === 'chat' || inbox === 'all' ? inbox : 'all';
  });
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(
    () => searchParams.get('query')
  );
  const [selectedFlyerLeadId, setSelectedFlyerLeadId] = useState<string | null>(
    () => searchParams.get('flyer')
  );
  const [handoffAutoReadyQueryId, setHandoffAutoReadyQueryId] = useState<string | null>(null);
  const [socketReady, setSocketReady] = useState(false);
  const [offer, setOffer] = useState<IncomingLeadQueryOffer | null>(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(20);
  const ackRef = useRef<((res: { accepted: boolean }) => void) | null>(null);
  const countdownRef = useRef<number | null>(null);
  const leadHandoffListenersAttachedRef = useRef(false);
  const attachLeadHandoffListenersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let mounted = true;

    const onSocketConnect = () => {
      if (!mounted) return;
      setSocketReady(true);
    };
    const onDisconnect = () => {
      if (!mounted) return;
      setSocketReady(false);
    };

    const onIncoming = (payload: IncomingLeadQueryOffer, ack: (res: { accepted: boolean }) => void) => {
      ackRef.current = ack;
      setOffer(payload);
      setSecondsLeft(20);
      setOfferOpen(true);
      if (getCachedNotificationPreferences().soundOnUrgent) {
        startRinging();
      }

      if (countdownRef.current != null) window.clearInterval(countdownRef.current);
      countdownRef.current = window.setInterval(() => {
        setSecondsLeft((s) => {
          const next = s - 1;
          if (next <= 0) {
            stopRinging();
            setOfferOpen(false);
            ackRef.current?.({ accepted: false });
            ackRef.current = null;
            if (countdownRef.current != null) {
              window.clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
            return 0;
          }
          return next;
        });
      }, 1000);
    };

    const onHandoff = (data: { queryId: string }) => {
      setSelectedQueryId(data.queryId);
      setSelectedFlyerLeadId(null);
      setHandoffAutoReadyQueryId(data.queryId);
      toast.success(`Handoff connected: ${data.queryId}`);
    };

    attachLeadHandoffListenersRef.current = () => {
      if (leadHandoffListenersAttachedRef.current) return;
      leadHandoffListenersAttachedRef.current = true;
      adminSocket.onSocketConnect(onSocketConnect);
      adminSocket.onDisconnect(onDisconnect);
      adminSocket.onIncomingLeadQuery(onIncoming);
      adminSocket.onLeadQueryHandoffSuccess(onHandoff);
      setSocketReady(adminSocket.connected);
    };

    return () => {
      mounted = false;
      stopRinging();
      if (countdownRef.current != null) window.clearInterval(countdownRef.current);
      attachLeadHandoffListenersRef.current = null;
      if (leadHandoffListenersAttachedRef.current) {
        adminSocket.offSocketConnect(onSocketConnect);
        adminSocket.offDisconnect(onDisconnect);
        adminSocket.offIncomingLeadQuery(onIncoming);
        adminSocket.offLeadQueryHandoffSuccess(onHandoff);
      }
      leadHandoffListenersAttachedRef.current = false;
    };
  }, []);

  const attachLeadHandoffListeners = useCallback(() => {
    attachLeadHandoffListenersRef.current?.();
  }, []);

  const acceptOffer = () => {
    stopRinging();
    setOfferOpen(false);
    const qid = offer?.queryId;
    ackRef.current?.({ accepted: true });
    ackRef.current = null;
    if (countdownRef.current != null) window.clearInterval(countdownRef.current);
    countdownRef.current = null;
    if (qid) {
      setSelectedQueryId(qid);
      setSelectedFlyerLeadId(null);
      setHandoffAutoReadyQueryId(qid);
    }
  };

  const declineOffer = () => {
    stopRinging();
    setOfferOpen(false);
    ackRef.current?.({ accepted: false });
    ackRef.current = null;
    if (countdownRef.current != null) window.clearInterval(countdownRef.current);
    countdownRef.current = null;
  };

  const backToInbox = () => {
    setSelectedQueryId(null);
    setSelectedFlyerLeadId(null);
    setHandoffAutoReadyQueryId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('query');
    next.delete('flyer');
    setSearchParams(next, { replace: true });
  };

  if (selectedFlyerLeadId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="outline" size="sm" onClick={backToInbox} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to inbox
          </Button>
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileImage className="h-5 w-5" />
            <span className="font-mono text-sm">{selectedFlyerLeadId}</span>
          </div>
        </div>
        <FlyerLeadDetail leadId={selectedFlyerLeadId} />
      </div>
    );
  }

  if (selectedQueryId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="outline" size="sm" onClick={backToInbox} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to inbox
          </Button>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Inbox className="h-5 w-5" />
            <span className="font-mono text-sm">{selectedQueryId}</span>
            <span className="text-xs text-muted-foreground">{socketReady ? '• live' : '• socket off'}</span>
          </div>
        </div>
        <QueryDetail
          key={selectedQueryId}
          queryId={selectedQueryId}
          initialReadyToChat={handoffAutoReadyQueryId === selectedQueryId}
          onInitialReadyConsumed={() => setHandoffAutoReadyQueryId(null)}
          onAttachLeadHandoffListeners={attachLeadHandoffListeners}
        />

        <IncomingLeadQueryModal
          open={offerOpen}
          offer={offer}
          secondsLeft={secondsLeft}
          onAccept={acceptOffer}
          onDecline={declineOffer}
        />
      </div>
    );
  }

  const filterButtons: { id: QueriesInboxFilter; label: string; icon: React.ReactNode }[] = [
    { id: 'all', label: 'All', icon: <Inbox className="h-4 w-4" /> },
    { id: 'chat', label: 'Chat', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'flyer', label: 'Flyer', icon: <FileImage className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Queries</h1>
          <p className="text-muted-foreground">
            Lead conversations and program flyer downloads from the website.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {socketReady
              ? 'Socket connected (after Ready to chat in a thread)'
              : 'Socket stays off until you turn on Ready to chat in a conversation'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterButtons.map((btn) => (
            <Button
              key={btn.id}
              type="button"
              size="sm"
              variant={inboxFilter === btn.id ? 'default' : 'outline'}
              className={cn('gap-2')}
              onClick={() => {
                setInboxFilter(btn.id);
                const next = new URLSearchParams(searchParams);
                if (btn.id === 'all') next.delete('inbox');
                else next.set('inbox', btn.id);
                setSearchParams(next, { replace: true });
              }}
            >
              {btn.icon}
              {btn.label}
            </Button>
          ))}
        </div>
      </div>

      {(inboxFilter === 'all' || inboxFilter === 'chat') && (
        <QueryList
          onSelect={(queryId) => {
            setSelectedQueryId(queryId);
            setSelectedFlyerLeadId(null);
            const next = new URLSearchParams(searchParams);
            next.set('query', queryId);
            next.delete('flyer');
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {(inboxFilter === 'all' || inboxFilter === 'flyer') && (
        <FlyerLeadList
          onSelect={(leadId) => {
            setSelectedFlyerLeadId(leadId);
            setSelectedQueryId(null);
            const next = new URLSearchParams(searchParams);
            next.set('flyer', leadId);
            next.set('inbox', 'flyer');
            next.delete('query');
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      <IncomingLeadQueryModal
        open={offerOpen}
        offer={offer}
        secondsLeft={secondsLeft}
        onAccept={acceptOffer}
        onDecline={declineOffer}
      />
    </div>
  );
};
